import { useItemSeenRef } from '@wix/data-tools-generic-events';
import type { Entities, Entity } from '@wix/data-tools-generic-events';
import { shouldLogBi } from './bi-logger';
// From the PURE module, not `use-generic-bi`: a suite that mocks that one to capture clicks would
// otherwise replace these too, and this hook would call `undefined` (see lib/bi-fields).
import { toAdditionalFields, toKebab } from './bi-fields';

/**
 * `itemSeen` (198:6) for a widget that came into VIEW — the impression half of a widget funnel, whose
 * other half is the `clickCta` (198:10) fired by `useGenericBi().logPageClickBi`.
 *
 * Returns a callback ref to attach to the element being measured. The platform hook does the
 * IntersectionObserver work: by default it fires ONCE, when 50% of the element is visible.
 *
 * **Why viewport and not a mount effect.** A widget rendered below the fold, or into a closed sheet, has
 * not been seen — and an impression that counts those inflates the denominator of every conversion rate
 * measured against it. This is also what makes it different from `logLoadFinish` (198:5), which says a
 * region finished fetching and can fire entirely off-screen.
 *
 * Wrapped rather than called directly at the call site so an impression gets the same two things every
 * other event in this app gets: the `shouldLogBi()` surface gate, and `toAdditionalFields` normalization.
 * Calling the platform hook raw would quietly opt out of both.
 */
export function useItemSeenBi(
  componentName: string,
  opts?: {
    entity?: Entity;
    /**
     * The root `entities` payload, when the impression is about more than the one entity `entity`
     * names. Left out, the library derives `{ [entity.type]: entity.id }` from `entity` — which is
     * what almost every call site wants, and why this is the rare override.
     */
    entities?: Entities;
    additionalFields?: Record<string, string | undefined>;
    disabled?: boolean;
  },
): (node: Element | null) => void {
  return useItemSeenRef(
    {
      // Kebab, like every click's componentName (`toKebab` in use-generic-bi) and like the repo's own
      // naming rule. Passed raw, an impression arrived as `band_offer_card` while its own click arrived
      // as `band-offer-card`, so the two halves of one widget's funnel did not join on component name.
      componentName: toKebab(componentName),
      // The business entity the impression is ABOUT — for a card in the chat, the conversation. Without
      // it an impression cannot be joined to the turn that produced it, which is the first thing anyone
      // asks of a card's numbers.
      ...(opts?.entity ? { entity: opts.entity } : {}),
      ...(opts?.entities ? { entities: opts.entities } : {}),
      ...(opts?.additionalFields ? { additionalFields: toAdditionalFields(opts.additionalFields) } : {}),
    },
    // `disabled` is the platform hook's own opt-out, so a gated-off surface never even observes.
    { disabled: opts?.disabled === true || !shouldLogBi() },
  );
}
